import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import Grid from '@mui/material/Grid';
import { isEqual } from 'lodash';
import { FilterReportType, MultiselectOption } from '@cbo/shared-library';
import useYupValidationResolver from '../../utils/formUtils/yupValidationResolver';
import { getSchemaResolver } from '../../utils/reportingUtils/filterUtils';
import { BaseFilter, SingleSelectGroup } from '../../components/GlobalFilterBar/types';
import SingleSelect from '../../components/GlobalFilterBar/SingleSelect/SingleSelect';
import { useFiltersQuery } from '../../../api/sales/queries';
import Multiselect from '../../components/GlobalFilterBar/Multiselect/Multiselect';
import SiteFilter from '../../components/GlobalFilterBar/SiteFilter/SiteFilter';
import { filterContainerStyling } from '../../components/GlobalFilterBar/styles';
import useSchedulingOffsetLocale from '../Scheduling/hooks/useSchedulingOffsetLocale';
import ScheduleWeekDateFilter from '../../components/GlobalFilterBar/DateFilter/ScheduleWeekDateFilter';

export interface ACAThresholdFilters extends BaseFilter {
  jobType: MultiselectOption[];
  employeeStatus: MultiselectOption[];
  hourlyThresholds: SingleSelectGroup[];
  datePickerNav: Dayjs;
}

type ACAThresholdReportFiltersProps = {
  filters: ACAThresholdFilters | undefined;
  showApproachingHourlyFilters?: boolean;
  hoursWorkedOptions?: SingleSelectGroup[];
  setFilters: (filters: ACAThresholdFilters) => void;
};

const employeeStatusControllerName = 'employeeStatus';
const hourlyThresholdsControllerName = 'hourlyThresholds';
const jobTypeControllerName = 'jobType';

function ACAThresholdReportFilters({
  filters,
  setFilters,
  hoursWorkedOptions,
  showApproachingHourlyFilters,
}: Readonly<ACAThresholdReportFiltersProps>) {
  const { t } = useTranslation();
  const { loading: jobTypeLoading, data: jobTypeResponse } = useFiltersQuery(FilterReportType.JOBTYPE);
  const { loading: employeeStatusLoading, data: employeeStatusResponse } = useFiltersQuery(
    FilterReportType.EMPLOYEE_STATUS
  );

  const formContext = useForm<ACAThresholdFilters>({
    mode: 'onChange',
    resolver: useYupValidationResolver(getSchemaResolver([jobTypeControllerName, employeeStatusControllerName])),
    defaultValues: {
      sites: [],
      datePickerNav: dayjs(),
      dateRange: {
        startDate: dayjs().locale('schedule-offset').startOf('week').format('YYYY-MM-DD'),
        endDate: dayjs().locale('schedule-offset').endOf('week').format('YYYY-MM-DD'),
      },
      jobType: [],
      employeeStatus: [],
      hourlyThresholds: [],
    },
  });
  const { watch, setValue, getValues } = formContext;
  const newFilterValues: ACAThresholdFilters = watch();
  const { loadingWorkWeekStart } = useSchedulingOffsetLocale();

  useEffect(() => {
    if (!isEqual(filters, newFilterValues)) {
      setFilters(newFilterValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newFilterValues]);

  useEffect(() => {
    if (loadingWorkWeekStart) return;
    setValue('dateRange', {
      startDate: dayjs().locale('schedule-offset').startOf('week').format('YYYY-MM-DD'),
      endDate: dayjs().locale('schedule-offset').endOf('week').format('YYYY-MM-DD'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingWorkWeekStart]);

  const handleNextWeekClicked = () => {
    const currentDatePicker = getValues('datePickerNav').add(1, 'week');
    setValue('datePickerNav', currentDatePicker);
  };

  const handlePreviousWeekClicked = () => {
    const currentDatePicker = getValues('datePickerNav').subtract(1, 'week');
    setValue('datePickerNav', currentDatePicker);
  };

  const jobTypeOptions: MultiselectOption[] = jobTypeResponse?.results ?? [];
  const employeeStatusOptions: MultiselectOption[] = employeeStatusResponse?.results ?? [];

  return (
    <FormContainer formContext={formContext}>
      <Grid container sx={filterContainerStyling}>
        <SiteFilter controllerName='sites' />
        <ScheduleWeekDateFilter handleNext={handleNextWeekClicked} handlePrev={handlePreviousWeekClicked} />
        {showApproachingHourlyFilters && (
          <>
            <Multiselect
              controllerName={jobTypeControllerName}
              plural={t('globalFilters.employeeTypes')}
              singular={t('globalFilters.employeeType')}
              options={jobTypeOptions}
              buttonText={t('globalFilters.employeeType')}
              titleText={t('globalFilters.employeeType')}
              defaultChecked={false}
              loading={jobTypeLoading}
            />
            <Multiselect
              controllerName={employeeStatusControllerName}
              plural={t('globalFilters.employeeStatuses')}
              singular={t('globalFilters.employeeStatus')}
              options={employeeStatusOptions}
              buttonText={t('globalFilters.employeeStatus')}
              titleText={t('globalFilters.employeeStatuses')}
              defaultChecked={false}
              loading={employeeStatusLoading}
            />
            {hoursWorkedOptions && (
              <SingleSelect
                controllerName={hourlyThresholdsControllerName}
                options={hoursWorkedOptions}
                buttonText={t('globalFilters.hourlyThreshold')}
                titleText={t('globalFilters.hourlyThreshold')}
              />
            )}
          </>
        )}
      </Grid>
    </FormContainer>
  );
}

ACAThresholdReportFilters.defaultProps = {
  hoursWorkedOptions: undefined,
  showApproachingHourlyFilters: false,
};

export default ACAThresholdReportFilters;
